import { useRef, useState } from 'react';
import { UploadCloud, Loader2, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { api } from '../api';
import { posliAktualizaciu } from '../lib/aktualizacia';

/**
 * RÝCHLA AKTUALIZÁCIA (.557) – štvorček v ľavom paneli, len administrátor.
 *
 * Oto: „sprav mi v CRM štvorček, nech mám rýchle nahranie aktualizácie,
 * lebo sa musím stále preklikávať do Nastavení a Aktualizácií – denne nahrám
 * niekedy aj 20–30."
 *
 * Klik = výber súboru a hneď sa nahráva; .zip sa dá na štvorček aj pretiahnuť.
 * Priebeh je v malom okne nad panelom. Po úspechu počká, kým server znova
 * odpovedá, a obnoví stránku – nová verzia sa tak hneď aj načíta.
 */
export default function RychlaAktualizacia() {
  const vstup = useRef(null);
  const [beh, setBeh] = useState(null);   // { subor, riadky, stav: 'bezi'|'hotovo'|'chyba', chyba }
  const [drag, setDrag] = useState(false);

  const pridaj = (t) => setBeh((b) => (b ? { ...b, riadky: [...b.riadky, t].slice(-6) } : b));

  const obnovPoStarte = async () => {
    /* Server sa po aktualizácii reštartuje – obnoviť skôr by ukázalo chybu. */
    for (let i = 0; i < 30; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try { await api.get('/api/update/status'); location.reload(); return; } catch { /* ešte nebeží */ }
    }
    location.reload();
  };

  const spusti = async (f) => {
    if (!f || beh?.stav === 'bezi') return;
    if (!/\.zip$/i.test(f.name)) {
      setBeh({ subor: f.name, riadky: [], stav: 'chyba', chyba: 'Nahrávajte súbor s príponou .zip' });
      return;
    }
    setBeh({ subor: f.name, riadky: [`Nahrávam ${(f.size / 1024 / 1024).toFixed(1)} MB…`], stav: 'bezi' });
    let chyba = null;
    try {
      const ok = await posliAktualizaciu(f, {
        onLog: pridaj,
        onError: (e) => { chyba = e; },
        onDone: (m) => pridaj(m),
      });
      if (ok) {
        setBeh((b) => ({ ...b, stav: 'hotovo' }));
        obnovPoStarte();
      } else {
        setBeh((b) => ({ ...b, stav: 'chyba', chyba: chyba || 'Aktualizácia neprebehla.' }));
      }
    } catch (e) {
      setBeh((b) => ({ ...b, stav: 'chyba', chyba: e.message }));
    }
  };

  return (
    <>
      <button type="button"
        title="Rýchla aktualizácia – kliknite a vyberte crm-….zip, alebo ho sem pretiahnite"
        onClick={() => vstup.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); spusti(e.dataTransfer.files?.[0]); }}
        className={`flex h-9 w-9 items-center justify-center rounded-lg ring-1 transition-colors ${
          beh?.stav === 'bezi' || drag
            ? 'bg-brand-600 text-white ring-brand-500'
            : 'bg-slate-800 text-slate-300 ring-slate-700 hover:bg-slate-700 hover:text-white'}`}>
        {beh?.stav === 'bezi' ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
      </button>
      <input ref={vstup} type="file" accept=".zip" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; spusti(f); }} />

      {beh && (
        <div className="fixed bottom-4 left-4 z-[60] w-80 rounded-xl bg-white p-3 text-sm shadow-xl ring-1 ring-slate-200">
          <div className="flex items-start gap-2">
            {beh.stav === 'bezi' && <Loader2 size={16} className="mt-0.5 shrink-0 animate-spin text-brand-600" />}
            {beh.stav === 'hotovo' && <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-600" />}
            {beh.stav === 'chyba' && <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600" />}
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold text-slate-900">{beh.subor}</div>
              <div className="text-xs text-slate-500">
                {beh.stav === 'bezi' ? 'Aktualizujem…'
                  : beh.stav === 'hotovo' ? 'Hotovo – čakám na server a obnovím stránku…'
                    : 'Aktualizácia neprebehla'}
              </div>
            </div>
            {beh.stav !== 'bezi' && (
              <button className="rounded p-0.5 text-slate-400 hover:text-slate-700" onClick={() => setBeh(null)} title="Zavrieť">
                <X size={14} />
              </button>
            )}
          </div>
          {!!beh.riadky.length && (
            <div className="mt-2 max-h-32 overflow-y-auto rounded bg-slate-50 p-2 font-mono text-[11px] leading-snug text-slate-600">
              {beh.riadky.map((r, i) => <div key={i}>{r}</div>)}
            </div>
          )}
          {beh.chyba && <div className="mt-2 text-xs text-red-700">{beh.chyba}</div>}
        </div>
      )}
    </>
  );
}
